import { useRequest } from 'umi';
import React, { useEffect, useState } from 'react';
import styles from '@/pages/camp/components/css/campCSS.less';
import type { TableProps } from 'antd';
import { Avatar, Button, Form, Input, message, Modal, Select, Table, Tabs, Tooltip } from 'antd';
import { pageVO } from '@/types/camp/admin/studentType';
import { createStudent, removeStudent, studentPage } from '@/utils/requests/camp/requests_admin';
import type { ColumnsType } from 'antd/es/table';
import { BatchInfoVO } from '@/types/camp/campIndex/indexTypes';
import { formatVideoDuration, getRealName } from '@/pages/camp/util/indexUtil';
import { CampStudyStateToText } from '@/pages/camp/util/campState';
import { getCampNodes } from '@/services/ConsoleRequest';
import { CampStudyStateOptions } from '@/types/camp/study/index';
import DateFormatMin from '@/pages/camp/util/dateFormatMin';
import NodeLog from './studentLog/nodeLog';
import VideoLog from './studentLog/videoLog';
import RewardLog from './studentLog/RewardLog';
import ScoreLog from './studentLog/scoreLog';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DiffOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';

type TableRowSelection<T extends object = object> = TableProps<T>['rowSelection'];

interface Props {
  batchInfo: BatchInfoVO;
}

const StudentTable: React.FC<Props> = ({ batchInfo }) => {
  const [dataSource, setDataSource] = useState<pageVO[]>([]);
  const [baseInfo, setBaseInfo] = useState<pageVO>();
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openMsg, setOpenMsg] = useState(false);
  const [current, setCurrent] = useState(1);
  const [size, setSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [studyNodeId, setStudyNodeId] = useState('');
  const { Search } = Input;
  const [hash, setHash] = useState<number>();
  const [nodeList, setNodeList] = React.useState<any>();
  const [nodeIds, setNodeIds] = React.useState<string[]>();
  const [selectedRowKeys, setSelectedRowKeys] = useState<[]>([]);
  const [scoreSort, setScoreSort] = useState<any>();
  const [durationSort, setDurationSort] = useState<any>();
  const [stateSort, setStateSort] = useState<any>();
  const [successMobileList, setSuccessMobileList] = useState<string[]>();
  const [failMobileList, setFailMobileList] = useState<string[]>();
  const [successNum, setSuccessNum] = useState<number>(0);
  const [failsNum, setFailsNum] = useState<number>(0);
  const { TextArea } = Input;
  const [form] = Form.useForm();
  const [isAdd, setIsAdd] = useState(true);

  // 添加
  const { run: createStudentRequest } = useRequest(
    (mobile: any) => {
      setOpenAdd(false);
      setLoading(true);
      let params = {
        campId: batchInfo.camp.id,
        mobileList: mobile.split(','),
      };
      return createStudent(batchInfo.id, params);
    },
    {
      manual: true,
      onSuccess: (res) => {
        let success = res.successMobileList.length;
        setSuccessNum(success);
        if (success > 0) {
          setSuccessMobileList(res.successMobileList);
          message.success(`成功添加 ${success} 名学员`);
        }

        let fails = res.failMobileList.length;
        setFailsNum(fails);
        setFailMobileList(res.failMobileList);
        setSuccessNum(success);
        setOpenMsg(true);

        getPageRequest();
      },
    },
  );

  // 删除
  const { run: removeStudentRequest } = useRequest(
    (id?: any) => {
      setLoading(true);
      let params = {
        idList: id ? [id] : selectedRowKeys,
      };
      return removeStudent(batchInfo.id, params);
    },
    {
      manual: true,
      onSuccess: (res) => {
        message.success('删除成功');
        getPageRequest();
      },
    },
  );

  const remove = (id?: any) => {
    if (id) {
      Modal.confirm({
        icon: <QuestionCircleOutlined />,
        content: (
          <div className="f16" style={{ paddingBottom: 16 }}>
            确认删除该学员的报名记录吗？
          </div>
        ),
        okText: '确定',
        cancelText: '取消',
        centered: true,
        onOk: () => {
          removeStudentRequest(id);
        },
      });
    } else {
      let count = selectedRowKeys.length;
      if (count < 1) {
        message.warning('请先选择要删除的学员');
      } else {
        Modal.confirm({
          icon: <QuestionCircleOutlined />,
          content: (
            <div className="f16" style={{ paddingBottom: 16 }}>
              确认删除所选<span className="colff4"> {count} </span>个学员的报名记录吗？
            </div>
          ),
          okText: '确定',
          cancelText: '取消',
          centered: true,
          onOk: () => {
            removeStudentRequest();
          },
        });
      }
    }
  };

  // 分页
  const { run: getPageRequest } = useRequest(
    () => {
      let params = {
        keyword: keyword,
        scoreSort: scoreSort,
        durationSort: durationSort,
        stateSort: stateSort,
        nodeIds: nodeIds,
        current: current,
        size: size,
      };
      return studentPage(batchInfo.id, params);
    },
    {
      manual: true,
      onSuccess: (res) => {
        setLoading(false);
        setDataSource(res.records);
        setSize(res.size);
        setTotal(res.total);
        setCurrent(res.current);
      },
    },
  );
  useEffect(() => {
    setLoading(true);
    getPageRequest();
  }, [current, keyword, nodeIds, scoreSort, durationSort, stateSort]);

  useEffect(() => {
    if (hash) {
      setLoading(true);
      setOpen(false);
      getPageRequest();
    }
  }, [hash]);

  // 获取节点接口
  const { run: resNodesRequest } = useRequest(
    (campId) => {
      return getCampNodes(campId);
    },
    {
      manual: true,
      onSuccess: (res) => {
        setNodeList(res);
      },
    },
  );

  useEffect(() => {
    resNodesRequest(batchInfo.camp.id);
    // 根据批次结束时间判断是否展示添加学员按钮
    const givenTime = new Date(batchInfo.enTime);
    const currentTime = new Date();
    if (currentTime >= givenTime) {
      setIsAdd(false);
    }
  }, []);

  const handleChange = (value: any) => {
    setNodeIds(value);
  };

  const onSelectChange = (newSelectedRowKeys: any) => {
    console.log('selectedRowKeys changed: ', newSelectedRowKeys);
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection: TableRowSelection<pageVO> = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const onChange: TableProps<pageVO>['onChange'] = (pagination, filters, sorter, extra) => {
    pagination.current && setCurrent(pagination.current);
    pagination.pageSize && setSize(pagination.pageSize);

    const { field, order } = Array.isArray(sorter) ? sorter[0] : sorter;
    if (field === 'ranking' || field === 'campScore') {
      setDurationSort(null);
      setScoreSort(order === 'descend' ? 1 : 0);
    }
    if (field === 'learningDuration') {
      setScoreSort(null);
      setDurationSort(order === 'descend' ? 1 : 0);
    }
    if (Array.isArray(filters.campStudyState)) {
      setStateSort(filters.campStudyState);
    } else {
      setStateSort(null);
    }

    // console.log('params', pagination, filters, sorter, extra);
    // console.log('field', field);
    // console.log('order', order);
    // console.log('campStudyState', filters.campStudyState);
  };

  const columns: ColumnsType<pageVO> = [
    {
      title: '排名',
      dataIndex: 'ranking',
      key: 'ranking',
      sorter: (a, b) => a.ranking - b.ranking,
    },
    {
      title: '昵称',
      dataIndex: 'username',
      key: 'username',
      render: (value, record) => (
        <div>
          <a
            href={`https://bbs.fanruan.com/u/${record.createdBy}`}
            onClick={(e) => {
              e.stopPropagation();
            }}
            target="_blank"
          >
            {value}
          </a>
        </div>
      ),
    },
    {
      title: '姓名',
      dataIndex: 'realName',
      key: 'realName',
      // render: (value) => (value ? value : '--'),
      render: (value, record) => {
        return getRealName(record.entryValue, value);
      },
    },
    {
      title: '手机号',
      dataIndex: 'mobile',
      key: 'mobile',
    },
    {
      title: '完成节点数',
      dataIndex: 'successNode',
      key: 'successNode',
      render: (value, record) => `${value}/${record.allNode}`,
    },
    {
      title: '学习时长',
      dataIndex: 'learningDuration',
      key: 'learningDuration',
      render: (value: any) => formatVideoDuration(value),
      sorter: (a, b) => a.learningDuration - b.learningDuration,
    },
    {
      title: '学分',
      dataIndex: 'campScore',
      key: 'campScore',
      sorter: (a, b) => a.campScore - b.campScore,
    },
    {
      title: '打卡状态',
      dataIndex: 'campStudyState',
      key: 'campStudyState',
      render: (value) => <CampStudyStateToText state={value} />,
      filters: CampStudyStateOptions,
      onFilter: (value, record) => record.campStudyState,
    },
    {
      title: '操作',
      dataIndex: 'tool',
      key: 'tool',
      align: 'center',
      render: (value, record) => (
        <div
          className="col5a5 hover008 cur_p f16"
          onClick={(e) => {
            e.stopPropagation();
            remove(record.id);
          }}
        >
          <DeleteOutlined />
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="w1200" style={{ marginBottom: 24 }}>
        <div className="com_cmap_card_box">
          <div className="flex_3" style={{ margin: '6px 0 18px' }}>
            <div className="flex_1">
              <div>
                <Search
                  placeholder="输入手机号/uid/昵称/姓名搜索"
                  onSearch={(value) => {
                    setKeyword(value);
                    setCurrent(1);
                  }}
                  allowClear
                  style={{ width: 300 }}
                />
              </div>
              <div className="flex_1" style={{ marginLeft: 40 }}>
                <span>完成节点：</span>
                <Select
                  mode="multiple"
                  maxTagCount="responsive"
                  style={{ width: 400 }}
                  placeholder="请选择，支持多选"
                  onChange={handleChange}
                  options={(nodeList || []).map((it: any) => ({
                    value: it.id,
                    label: it.name,
                  }))}
                />
              </div>
            </div>
            <div className="flex_1" style={{ gap: 10 }}>
              <div
                onClick={() => remove()}
                className="flex_1 com_camp_btn_def"
                style={{ width: 106, height: 32, gap: 4 }}
              >
                <DeleteOutlined />
                <span>删除学员</span>
              </div>
              {isAdd && (
                <div
                  onClick={() => setOpenAdd(true)}
                  className="flex_1 com_camp_btn"
                  style={{ width: 106, height: 32, gap: 4 }}
                >
                  <DiffOutlined />
                  <span>添加学员</span>
                </div>
              )}
            </div>
          </div>

          <Table
            rowKey={(record) => record.id}
            rowSelection={rowSelection}
            columns={columns}
            dataSource={dataSource}
            loading={loading}
            pagination={{
              total: total,
              showTotal: (total, range) => `共 ${total} 条数据`,
              pageSize: size,
              current: current,
              hideOnSinglePage: true,
              showQuickJumper: true,
            }}
            onChange={onChange}
            onRow={(record) => {
              return {
                onClick: (event) => {
                  setOpen(true);
                  setStudyNodeId(record.id);
                  setBaseInfo(record);
                },
              };
            }}
          />
        </div>
      </div>

      <Modal
        title={'添加学员结果'}
        open={openMsg}
        width={560}
        destroyOnClose={true}
        centered
        onCancel={() => {
          setOpenMsg(false);
        }}
        footer={null}
      >
        <div>
          <div style={{ marginTop: 20 }} className="col808">
            {successNum > 0 && (
              <div className="col172">
                <CheckCircleOutlined className="col52c" />
                <span style={{ marginLeft: 4 }}>{successNum}名学员添加成功</span>
              </div>
            )}
            {failsNum > 0 && (
              <div>
                <div className="flex_1" style={{ margin: '6px 0 6px' }}>
                  <span className="col172">
                    <CloseCircleOutlined className="colff3" />
                    <span style={{ marginLeft: 4 }}>{failsNum}名学员添加失败</span>
                  </span>
                  <span>
                    <Tooltip
                      placement="top"
                      align={{ offset: [0, -4] }}
                      title={
                        <div className="flex_4_2">
                          <span> 可能的原因：</span>
                          <span>· 手机号格式错误</span>
                          <span>· 学员已经报名了本批次</span>
                          <span> · 学员已经成功打卡过或正在打卡本打卡营</span>
                        </div>
                      }
                    >
                      <QuestionCircleOutlined className="col808 f14" style={{ marginLeft: 4 }} />
                    </Tooltip>
                  </span>
                </div>
                <div className="col172 f14">{failMobileList && failMobileList.join(',')}</div>
                <div style={{ marginTop: 10 }}>
                  <Button
                    type="link"
                    onClick={() => {
                      navigator.clipboard.writeText(`${failMobileList}`);
                      message.success('复制成功');
                    }}
                  >
                    一键复制
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        title={'手动添加学员'}
        open={openAdd}
        width={560}
        destroyOnClose={true}
        centered
        onCancel={() => {
          setOpenAdd(false);
        }}
        footer={null}
      >
        <div>
          <div style={{ marginTop: 40 }} className="col808">
            <div>1.请在下方粘贴学员手机号，多个手机号之间用英文逗号“,”分割</div>
            <div>
              2.如果学员还没有注册帆软账号，将自动为学员用手机号注册账号，请引导学员用手机号+验证码登录
            </div>
          </div>
          <div style={{ padding: '16px 0 24px' }}>
            <Form
              form={form}
              name="basic"
              onFinish={(values: any) => {
                createStudentRequest(values.mobile);
                form.resetFields();
              }}
              autoComplete="off"
            >
              <Form.Item name="mobile" rules={[{ required: true, message: '请输入手机号!' }]}>
                <TextArea rows={8} placeholder="请输入" />
              </Form.Item>

              <div style={{ textAlign: 'right' }}>
                <Button
                  onClick={() => {
                    setOpenAdd(false);
                  }}
                >
                  取消
                </Button>
                <Button type="primary" htmlType="submit" style={{ marginLeft: 10 }}>
                  确认
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </Modal>

      <Modal
        title={''}
        open={open}
        style={{ height: '94vh' }}
        destroyOnClose={true}
        wrapClassName={styles.my_modal}
        centered
        width={1200}
        onOk={() => {}}
        onCancel={() => {
          setOpen(false);
        }}
        footer={null}
      >
        <div style={{ height: '94vh', overflow: 'hidden' }}>
          <div style={{ padding: '50px 24px 10px 24px' }}>
            <div className="flex_3">
              <div className="flex_1">
                <div>
                  <a href={`https://bbs.fanruan.com/u/${baseInfo?.createdBy}`} target="blank">
                    <Avatar
                      size={66}
                      src={`https://bbs.fanruan.com/uc_server/avatar.php?uid=${baseInfo?.createdBy}&amp;size=small`}
                    />
                  </a>
                </div>
                <div style={{ marginLeft: 16 }}>
                  <div className="f18" style={{ marginBottom: 4 }}>
                    {baseInfo?.username}
                  </div>
                  <div className="flex_1" style={{ gap: 20 }}>
                    <div>
                      <span className="colc5c">用户ID：</span>
                      <span className="col5a5">{baseInfo?.createdBy}</span>
                    </div>
                    <div>
                      <span className="colc5c">真实姓名：</span>
                      <span className="col5a5">
                        {getRealName(baseInfo?.entryValue, baseInfo?.realName)}
                      </span>
                    </div>
                    <div>
                      <span className="colc5c">手机号：</span>
                      <span className="col5a5">{baseInfo?.mobile}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.manage_student_details_rt}>
                <div className={styles.manage_student_details_rt_item}>
                  <div className="col808 f12">学习时长</div>
                  <div>
                    <DateFormatMin
                      duration={baseInfo?.learningDuration || 0}
                      showH={true}
                      showS={true}
                    />
                  </div>
                </div>
                <div className={styles.manage_student_details_rt_item}>
                  <div className="col808 f12">打卡进度</div>
                  <div>
                    <span className="f18">{baseInfo?.successNode}</span>
                    <span className="col808">/{baseInfo?.allNode}</span>
                  </div>
                </div>
                <div className={styles.manage_student_details_rt_item}>
                  <div className="col808 f12">获得学分</div>
                  <div>
                    <span className="f18">{baseInfo?.campScore}</span>
                  </div>
                </div>
                <div className={styles.manage_student_details_rt_item}>
                  <div className="col808 f12">当前排名</div>
                  <div>
                    <span className="f18">{baseInfo?.ranking}</span>
                    <span className="col808">/{total}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <Tabs
              defaultActiveKey="1"
              className="com_camp_tab new_tab_f16"
              items={[
                {
                  label: '打卡信息',
                  key: '1',
                  children: baseInfo?.id && (
                    <NodeLog batchId={batchInfo.id} signUpId={baseInfo?.id} />
                  ),
                },
                {
                  label: '视频观看信息',
                  key: '2',
                  children: baseInfo?.id && (
                    <VideoLog batchId={batchInfo.id} signUpId={baseInfo?.id} />
                  ),
                },
                {
                  label: '学分获取记录',
                  key: '3',
                  children: baseInfo?.createdBy && (
                    <ScoreLog batchId={batchInfo.id} uid={baseInfo?.createdBy} />
                  ),
                },
                {
                  label: '奖励领取记录',
                  key: '4',
                  children: baseInfo?.createdBy && (
                    <RewardLog
                      batchId={batchInfo.id}
                      uid={baseInfo?.createdBy}
                      nodeList={nodeList}
                    />
                  ),
                },
              ]}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default StudentTable;
